import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { hashPassword } from '../../utils/security';

const AuthContext = createContext();
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

// Refresh token silently 15 minutes before expiry
const AUTO_REFRESH_BEFORE_EXPIRY_SECONDS = 15 * 60;

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const refreshTimerRef = useRef(null);
  const refreshFnRef = useRef(null);
  const setupSessionTimersRef = useRef(null);

  // Clear all timers
  const clearTimers = useCallback(() => {
    if (refreshTimerRef.current) {
      clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }
  }, []);

  const logout = useCallback(async () => {
    clearTimers();

    // Call backend to clear HTTP-only cookie
    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout API error:', error);
    }

    localStorage.removeItem('tokenExpiresAt');
    setUser(null);
  }, [clearTimers]);

  const refreshToken = useCallback(async () => {
    setIsRefreshing(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        credentials: 'include',  // Send HTTP-only cookie
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        // New token is in HTTP-only cookie
        setUser(data.user);

        if (data.expires_in) {
          setupSessionTimersRef.current?.(data.expires_in);
        }

        setIsRefreshing(false);
        return true;
      }

      // Token already expired or invalid
      setIsRefreshing(false);
      logout();
      return false;
    } catch (error) {
      console.error('Error refreshing token:', error);
      setIsRefreshing(false);
      logout();
      return false;
    }
  }, [logout]);

  // Set up silent auto-refresh timer based on expiry time
  const setupSessionTimers = useCallback((expiresInSeconds) => {
    clearTimers();

    if (expiresInSeconds <= 0) return;

    const refreshDelayMs = Math.max(
      0,
      (expiresInSeconds - AUTO_REFRESH_BEFORE_EXPIRY_SECONDS) * 1000
    );

    refreshTimerRef.current = setTimeout(() => {
      if (refreshFnRef.current) {
        refreshFnRef.current();
      }
    }, refreshDelayMs);

    // Store expiry time in localStorage for persistence across page refreshes
    const expiresAt = Date.now() + (expiresInSeconds * 1000);
    localStorage.setItem('tokenExpiresAt', expiresAt.toString());

    console.log(`Session Timer Setup: Expires in ${expiresInSeconds}s, Auto refresh in ${refreshDelayMs / 1000}s`);
  }, [clearTimers]);

  useEffect(() => {
    refreshFnRef.current = refreshToken;
  }, [refreshToken]);

  useEffect(() => {
    setupSessionTimersRef.current = setupSessionTimers;
  }, [setupSessionTimers]);

  // Fetch user profile using HTTP-only cookie (no token parameter needed)
  const fetchUserProfile = async () => {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
        credentials: 'include'  // Send HTTP-only cookie
      });
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        return true;
      } else {
        // Cookie invalid or expired
        localStorage.removeItem('tokenExpiresAt');
        return false;
      }
    } catch (error) {
      console.error('Error fetching user profile:', error);
      localStorage.removeItem('tokenExpiresAt');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Check for existing session on mount
  useEffect(() => {
    const expiresAt = localStorage.getItem('tokenExpiresAt');

    if (expiresAt) {
      const remainingMs = parseInt(expiresAt) - Date.now();

      if (remainingMs > 0) {
        // Session might still be valid, try to fetch user profile
        console.log(`Session Restore: Checking cookie, remaining ${remainingMs / 1000}s`);
        fetchUserProfile().then(success => {
          if (success) {
            setupSessionTimers(Math.floor(remainingMs / 1000));
          }
        });
      } else {
        // Session expired, clear everything
        console.log('Session Restore: Session expired');
        localStorage.removeItem('tokenExpiresAt');
        setLoading(false);
      }
    } else {
      // No stored expiry, try to fetch profile anyway (cookie might exist)
      fetchUserProfile();
    }

    return () => clearTimers();
  }, [setupSessionTimers, clearTimers]);

  /**
   * Login with email and password.
   * Password is SHA-256 hashed before transmission.
   * CAPTCHA token is sent if provided.
   */
  const login = async (email, password, captchaToken = null) => {
    try {
      const hashedPassword = await hashPassword(password);
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        credentials: 'include',  // Enable cookie handling
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password: hashedPassword,
          captcha_token: captchaToken
        }),
      });

      if (response.ok) {
        const data = await response.json();

        // ── MFA gate (Phase 9) ───────────────────────────────────────────
        if (data.mfa_required) {
          return {
            success: false,
            mfaRequired: true,
            mfaToken: data.mfa_token,
            availableFactors: data.available_factors || [],
            userRole: data.user_role || '',
          };
        }

        // Token is now in HTTP-only cookie (invisible to JavaScript)
        setUser(data.user);

        // Set up session timers
        if (data.expires_in) {
          setupSessionTimers(data.expires_in);
        }

        return { success: true };
      } else {
        const error = await response.json();
        return { success: false, error: error.detail };
      }
    } catch (error) {
      return { success: false, error: 'Network error' };
    }
  };

  const completeMfaLogin = (data) => {
    // Called by MfaChallenge after a successful /api/auth/mfa/verify.
    // The response body has the same shape as the no-MFA login response.
    setUser(data.user);
    if (data.expires_in) {
      setupSessionTimers(data.expires_in);
    }
  };

  const value = {
    user,
    login,
    logout,
    loading,
    refreshToken,
    completeMfaLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};